import { BidAsk, BidsAsks } from '../types/orderbook';
import { range } from './arrayHelpers';
import { Gwei } from './gwei';
import { divide, multiply } from './math';

/**
 * Sorts bids or asks in descending order
 * @param bidsAsks
 * @returns
 */
export const sortBidsOrAsks = (bidsAsks: BidAsk[]) =>
  bidsAsks.sort((a, b) => {
    if (b[0] < a[0]) return -1;
    if (b[0] > a[0]) return 1;
    return 0;
  });

/**
 * Gets the best bid and ask from the orderbook
 * @param bidsAsks Assumes descending order
 * @returns
 */
export const getBestBidAsk = (bidsAsks: BidsAsks) => {
  const bestBid = bidsAsks.bids.at(0);
  const bestAsk = bidsAsks.asks.at(-1);
  return { bestBid, bestAsk };
};

/**
 * Checks if an order book is crossed (best bid >= best ask)
 * @param orderBook The order book to check
 * @returns Object with isCrossed flag and best prices
 */
export const checkCrossedOrderBook = (orderBook: BidsAsks) => {
  const { bestBid, bestAsk } = getBestBidAsk(orderBook);

  if (!bestBid || !bestAsk) {
    return { isCrossed: false };
  }

  const isCrossed = bestBid[0] >= bestAsk[0];
  return {
    isCrossed,
    bestBidPrice: bestBid[0],
    bestAskPrice: bestAsk[0],
  };
};

export const mergeBidsAsks = (old: BidsAsks, current: BidsAsks): BidsAsks => {
  const updateSide = (oldSide: BidAsk[], newSide: BidAsk[]): BidAsk[] => {
    const priceMap = new Map<bigint, bigint>(oldSide);

    newSide.forEach(([newPrice, newQty]) => {
      if (newQty !== 0n) {
        priceMap.set(newPrice, newQty);
      } else {
        priceMap.delete(newPrice);
      }
    });

    return Array.from(priceMap.entries());
  };

  return {
    bids: sortBidsOrAsks(updateSide(old.bids, current.bids)),
    asks: sortBidsOrAsks(updateSide(old.asks, current.asks)),
  };
};

export const calcPriceImpactBasedOnSize = (
  bidsAsks: BidsAsks | undefined,
  side: 'long' | 'short',
  size: bigint
): {
  executionPrice?: bigint;
  priceImpact?: bigint;
  midPrice?: bigint; // Mid price can be available even if there is not enough liquidity
  enoughLiquidity: boolean;
} => {
  if (!bidsAsks) return { enoughLiquidity: false };

  const midPrice = calcMidPriceFromOrderbook(bidsAsks);
  if (!midPrice) return { enoughLiquidity: false };

  const calcExecutionPriceLiq = () => {
    let remainingSize = size;
    let totalCost = 0n;

    const orderbook =
      side === 'long'
        ? [...bidsAsks.asks].sort((a, b) => Number(a[0] - b[0])) // Sort ascending for asks to get best prices first
        : bidsAsks.bids; // Already in descending order for bids (best prices first)

    for (const [price, quantity] of orderbook) {
      const fillSize = Gwei.min(remainingSize, quantity);
      totalCost += multiply(fillSize, price);
      remainingSize -= fillSize;

      if (remainingSize <= 0n) {
        break;
      }
    }
    if (remainingSize > 0n) {
      return undefined;
    }
    return divide(totalCost, size);
  };

  const calcWithBestAskAndBid = () => {
    const { bestAsk, bestBid } = getBestBidAsk(bidsAsks);
    const executionPrice = side === 'long' ? bestAsk?.at(0) : bestBid?.at(0);
    if (!executionPrice) {
      return undefined;
    }
    return executionPrice;
  };

  const executionPrice = size === 0n ? calcWithBestAskAndBid() : calcExecutionPriceLiq();
  if (!executionPrice) {
    return { enoughLiquidity: false, midPrice };
  }
  const priceImpact =
    side === 'long'
      ? divide(executionPrice - midPrice, midPrice)
      : divide(midPrice - executionPrice, midPrice);
  return {
    midPrice,
    executionPrice,
    priceImpact,
    enoughLiquidity: true,
  };
};

export function calcPriceImpact(
  price: bigint,
  midPrice: bigint,
  type: 'bid' | 'ask'
): bigint | null {
  if (midPrice === 0n) {
    return null;
  }
  const priceDiff = type === 'bid' ? midPrice - price : price - midPrice;
  return divide(priceDiff, midPrice);
}

export const calcMidPriceFromOrderbook = (bidsAsks: BidsAsks | undefined): bigint | null => {
  // If both bids and asks are empty, return null
  if (!bidsAsks || (bidsAsks.bids.length === 0 && bidsAsks.asks.length === 0)) {
    return null;
  }

  const { bestAsk, bestBid } = getBestBidAsk(bidsAsks);

  // If there are no bids, return the price of the first ask
  if (bidsAsks.bids.length === 0) {
    if (bestAsk) {
      return bestAsk[0];
    }
    return null;
  }

  // If there are no asks, return the price of the first bid
  if (bidsAsks.asks.length === 0) {
    if (bestBid) {
      return bestBid[0];
    }
    return null;
  }

  // Calculate the mid price as the average of the first bid and ask prices
  if (bestAsk && bestBid) {
    const midPrice = divide(bestAsk[0] + bestBid[0], Gwei.toGwei(2));
    return midPrice;
  }

  return null;
};

export const calcSpread = (bidsAsks: BidsAsks) => {
  const { bestAsk, bestBid } = getBestBidAsk(bidsAsks);
  const bestAskPrice = bestAsk?.at(0);
  const bestBidPrice = bestBid?.at(0);

  if (!bestAskPrice || !bestBidPrice) return;

  const spread = bestAskPrice - bestBidPrice;
  return spread;
};

export function calcSpreadPercentage(bidsAsks: BidsAsks) {
  const { bestAsk, bestBid } = getBestBidAsk(bidsAsks);
  const bestAskPrice = bestAsk?.at(0);
  const bestBidPrice = bestBid?.at(0);
  if (!bestAskPrice || !bestBidPrice) return;

  const midPrice = divide(bestAskPrice + bestBidPrice, Gwei.toGwei(2));
  const spread = bestAskPrice - bestBidPrice;
  return divide(spread, midPrice);
}

export const populateEmpty = (
  bidsOrAsks: BidAsk[],
  type: 'bid' | 'ask',
  minRow: number
): (BidAsk | [undefined, undefined])[] => {
  if (bidsOrAsks.length >= minRow) {
    return bidsOrAsks;
  }
  const emptyRows: [undefined, undefined][] = range(minRow - bidsOrAsks.length).map(() => [
    undefined,
    undefined,
  ]);

  return type === 'bid' ? [...bidsOrAsks, ...emptyRows] : [...emptyRows, ...bidsOrAsks];
};

/** Get a price rounded to the nearest group size, rounding based on the order type. */
export const getGroupedPrice = (price: bigint, groupSize: bigint, type: 'bid' | 'ask') => {
  return type === 'bid' ? Gwei.floor(price, groupSize) : Gwei.ceil(price, groupSize);
};

/**
 * Groups orderbook levels by the specified price increment
 * @param bidsAsks Bids and asks to group
 * @param groupSize The size to group prices by (multiple of tickSize)
 * @returns New bids and asks with grouped price levels
 */
export const groupOrderbook = (bidsAsks: BidsAsks, groupSize: bigint): BidsAsks => {
  const groupPriceLevels = (levels: BidAsk[], type: 'bid' | 'ask'): BidAsk[] => {
    const groupedLevels = new Map<bigint, bigint>();

    levels.forEach(([price, quantity]) => {
      // Round price to nearest group size using precise math
      const groupedPrice = getGroupedPrice(price, groupSize, type);
      const currentQuantity = groupedLevels.get(groupedPrice) || 0n;
      groupedLevels.set(groupedPrice, currentQuantity + quantity);
    });

    return Array.from(groupedLevels.entries()).map(
      ([price, quantity]): BidAsk => [price, quantity]
    );
  };

  return {
    bids: sortBidsOrAsks(groupPriceLevels(bidsAsks.bids, 'bid')),
    asks: sortBidsOrAsks(groupPriceLevels(bidsAsks.asks, 'ask')),
  };
};
